import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  PreHeader,
  Stack,
  Switch,
  Text,
  Textarea,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import {
  MdCheck,
  MdClose,
  MdContentCopy,
  MdEdit,
  MdOpenInNew,
  MdSave,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import { ServerConfig, serverService } from "services/serverService";
import {
  WidgetConfig,
  isCustomTriggerDisplay,
  widgetService,
} from "services/widgetService";
import {
  KbDocumentDto,
  KbDocumentStatus,
  KbFolderTreeDto,
  KbWebSourceDto,
} from "types/knowledgeBase";
import WidgetSelector from "views/admin/widgets/components/WidgetSelector";
import ContextInjectionPanel from "./components/ContextInjectionPanel";
import KbTreePicker from "./components/KbTreePicker";

const CHAT_WIDGET_URL = process.env.REACT_APP_CHAT_WIDGET_URL;
const WIDGET_SCRIPT_URL = process.env.REACT_APP_WIDGET_SCRIPT_URL;
const API_BASE_URL = process.env.REACT_APP_BASE_API_URL;

interface InlineEmbedOptions {
  enabled: boolean;
  container: string;
  portal: boolean;
}

const buildEmbedScript = (
  widget: WidgetConfig,
  injectedServers: ServerConfig[],
  inline?: InlineEmbedOptions,
): string => {
  const config: Record<string, any> = {
    applicationToken: widget.applicationToken,
    userId: "your_customer_id",
    userDisplayName: "your_customer_display_name",
    userEmail: "your_customer_email",
    userCompanyId: "your_customer_company_id",
    userCompanyName: "your_customer_company_name",
    handoffUserId: "your_handoff_user_id",
  };
  if (injectedServers.length > 0) {
    config.serverAuthorizations = injectedServers.map((server) => {
      let authDataExample: Record<string, string> = {};
      if (server.authorizationType === "BasicAuth") {
        authDataExample = {
          username: "customer_username",
          password: "customer_password",
        };
      } else if (server.authorizationType === "OAuth") {
        authDataExample = { accessToken: "customer_oauth_access_token" };
      } else if (server.authorizationType === "CustomAuthEndpoint") {
        if (server.autoTokenRequestPlaceholders) {
          try {
            const placeholders = JSON.parse(
              server.autoTokenRequestPlaceholders,
            ) as Record<string, string>;
            Object.keys(placeholders).forEach((key) => {
              authDataExample[key] = `customer_${key.toLowerCase()}`;
            });
          } catch {
            authDataExample = { auth_token: "customer_auth_token" };
          }
        } else {
          authDataExample = { auth_token: "customer_auth_token" };
        }
      } else {
        if (server.dynamicAuthHeaders) {
          try {
            const headers = JSON.parse(server.dynamicAuthHeaders);
            headers.forEach((h: { name: string }) => {
              authDataExample[h.name] = `customer_${h.name.toLowerCase()}`;
            });
          } catch {
            authDataExample = { Authorization: "Bearer customer_token" };
          }
        } else {
          authDataExample = { Authorization: "Bearer customer_token" };
        }
      }
      return { serverId: server.id, authData: authDataExample };
    });
  }
  // Inline embed — the widget mounts into a host container instead of floating.
  // `container` is page-specific, so we emit a placeholder <div> the host edits.
  let containerHtml = "";
  if (inline?.enabled) {
    const selector = inline.container.trim() || "#strikeup-widget";
    config.displayType = "inline";
    config.container = selector;
    if (inline.portal) {
      config.portalDisplayType = "inline";
    }
    const elementId = selector.replace(/^#/, "");
    containerHtml = `<div id="${elementId}" style="width: 100%; height: 600px;"></div>
`;
  }
  return `${containerHtml}<script src="${WIDGET_SCRIPT_URL}" data-api-url="${API_BASE_URL}"></script>
<script>
  window.initStrikeUpChatWidget(${JSON.stringify(config, null, 2)});
</script>`;
};

const EmbedPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedWidgetId, setSelectedWidgetId] = useState<string | undefined>(
    () => {
      const params = new URLSearchParams(window.location.search);
      return params.get("widgetId") ?? undefined;
    },
  );

  const [widget, setWidget] = useState<WidgetConfig | null>(null);
  const [widgetServers, setWidgetServers] = useState<ServerConfig[]>([]);
  const [allServers, setAllServers] = useState<ServerConfig[]>([]);
  const [allWebSources, setAllWebSources] = useState<KbWebSourceDto[]>([]);
  const [allDocuments, setAllDocuments] = useState<KbDocumentDto[]>([]);
  const [folderTree, setFolderTree] = useState<KbFolderTreeDto[]>([]);
  const [injectedServerIds, setInjectedServerIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  // Inline embed snippet options
  const [inlineEmbed, setInlineEmbed] = useState(false);
  const [inlineContainer, setInlineContainer] = useState("#strikeup-widget");
  const [inlinePortal, setInlinePortal] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState(
    "What would you like to know about this page?",
  );
  // autoSend turns the opening message into the visitor's own first message —
  // it is sent immediately in a new session instead of waiting for a reply.
  const [triggerAutoSend, setTriggerAutoSend] = useState(false);
  const [copiedTriggerBasic, setCopiedTriggerBasic] = useState(false);
  const [copiedTriggerMessage, setCopiedTriggerMessage] = useState(false);
  const [copiedTriggerDevCtx, setCopiedTriggerDevCtx] = useState(false);
  const [copiedTriggerIsOpen, setCopiedTriggerIsOpen] = useState(false);
  const [copiedTriggerClose, setCopiedTriggerClose] = useState(false);
  const [devContextExample, setDevContextExample] = useState(
    "Visitor clicked 'Explain this chart' on the Revenue by week graph, range: last 12 weeks.",
  );

  const copySnippet = async (text: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const triggerOpenBasic = `<button onclick="window.StrikeUpChat?.open()">\n  Open AI Assistant\n</button>`;
  const triggerOpenMessage = `<button onclick="window.StrikeUpChat?.open({ message: '${triggerMessage.replace(
    /'/g,
    "\\'",
  )}'${triggerAutoSend ? ", autoSend: true" : ""} })">\n  Ask AI\n</button>`;
  const triggerOpenDevContext = `<button onclick="window.StrikeUpChat?.open({\n  message: '${triggerMessage.replace(/'/g, "\\'")}',\n  openContext: '${devContextExample.replace(/'/g, "\\'")}'\n})">\n  Ask AI\n</button>`;
  const triggerClose = `<button onclick="window.StrikeUpChat?.close()">\n  Close AI Assistant\n</button>`;
  const triggerIsOpen = `askAiButton.onclick = () => {\n  if (window.StrikeUpChat?.isOpen()) {\n    window.StrikeUpChat.close();\n  } else {\n    window.StrikeUpChat?.open();\n  }\n};`;

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editServerIds, setEditServerIds] = useState<string[]>([]);
  const [editKbDocumentIds, setEditKbDocumentIds] = useState<string[]>([]);
  const [editKbWebSourceIds, setEditKbWebSourceIds] = useState<string[]>([]);
  const [editKbFolderIds, setEditKbFolderIds] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // Sticky banner
  const [selectorInView, setSelectorInView] = useState(true);
  const [bannerLeft, setBannerLeft] = useState(0);
  const selectorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = () => {
      if (selectorRef.current)
        setSelectorInView(
          selectorRef.current.getBoundingClientRect().bottom > 0,
        );
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, []);

  useEffect(() => {
    const update = () => {
      if (contentRef.current)
        setBannerLeft(contentRef.current.getBoundingClientRect().left);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (!selectedWidgetId) {
      setWidget(null);
      setWidgetServers([]);
      setAllServers([]);
      setAllWebSources([]);
      setAllDocuments([]);
      setFolderTree([]);
      setInjectedServerIds([]);
      setIsEditing(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setIsEditing(false);
        const [
          widgetData,
          allServersData,
          webSourcesData,
          documentsData,
          treeData,
        ] = await Promise.all([
          widgetService.getWidgetById(selectedWidgetId),
          serverService.getAllServers(),
          knowledgeBaseService.getWebSources(),
          knowledgeBaseService.getDocuments({ pageSize: 500 }),
          knowledgeBaseService.getFolderTree(),
        ]);
        setWidget(widgetData);
        setAllServers(allServersData);
        setAllWebSources(webSourcesData);
        setAllDocuments(
          documentsData.items.filter(
            (d: KbDocumentDto) => d.status === KbDocumentStatus.Indexed,
          ),
        );
        setFolderTree(treeData);
        const mappedIds = (widgetData.serverMappings ?? []).map(
          (m) => m.serverId,
        );
        const mapped = allServersData.filter(
          (s) =>
            s.id &&
            mappedIds.includes(s.id) &&
            s.authorizationType &&
            s.authorizationType !== "None",
        );
        setWidgetServers(mapped);
        setInjectedServerIds([]);
      } catch {
        setWidget(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedWidgetId]);

  const handleSelect = (id: string) => {
    setSelectedWidgetId(id || undefined);
    const params = new URLSearchParams(window.location.search);
    if (id) {
      params.set("widgetId", id);
    } else {
      params.delete("widgetId");
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const toggleInjected = (serverId: string, checked: boolean) => {
    setInjectedServerIds((prev) =>
      checked ? [...prev, serverId] : prev.filter((id) => id !== serverId),
    );
  };

  const handleCopy = async () => {
    if (!widget) return;
    const injected = widgetServers.filter((s) =>
      injectedServerIds.includes(s.id!),
    );
    try {
      await navigator.clipboard.writeText(
        buildEmbedScript(widget, injected, {
          enabled: inlineEmbed,
          container: inlineContainer,
          portal: inlinePortal,
        }),
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const enterEditMode = () => {
    if (!widget) return;
    setEditName(widget.name || "");
    setEditDescription(widget.description || "");
    setEditServerIds(widget.serverIds ?? []);
    setEditKbDocumentIds(widget.kbDocumentIds ?? []);
    setEditKbWebSourceIds(widget.kbWebSourceIds ?? []);
    setEditKbFolderIds(widget.kbFolderIds ?? []);
    setEditError("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditError("");
  };

  const handleSave = async () => {
    if (!widget?.id) return;
    if (!editName.trim()) {
      setEditError("Widget name is required.");
      return;
    }
    if (editServerIds.length === 0) {
      setEditError("At least one toolkit must be selected.");
      return;
    }
    try {
      setEditSaving(true);
      setEditError("");
      const updated = await widgetService.updateWidget(widget.id, {
        name: editName.trim(),
        description: editDescription || undefined,
        serverIds: editServerIds,
        kbDocumentIds: editKbDocumentIds,
        kbWebSourceIds: editKbWebSourceIds,
        kbFolderIds: editKbFolderIds,
      });
      setWidget(updated);
      // Refresh server badges
      const mappedIds = (updated.serverMappings ?? []).map((m) => m.serverId);
      const mapped = allServers.filter(
        (s) =>
          s.id &&
          mappedIds.includes(s.id) &&
          s.authorizationType &&
          s.authorizationType !== "None",
      );
      setWidgetServers(mapped);
      setIsEditing(false);
    } catch (err: any) {
      setEditError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to save widget.",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const injectedServers = widgetServers.filter((s) =>
    injectedServerIds.includes(s.id!),
  );

  return (
    <Stack gap={5} w="full" ref={contentRef}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={4} mb={2}>
        <VStack align="start" gap={1}>
          <Heading size="lg" color="var(--text-primary)">
            Embed
          </Heading>
          <Text fontSize="sm" color="var(--text-secondary-color)" m={0}>
            Get the embed code for your chat widget.
          </Text>
        </VStack>
        <AskStrikeUpButton context={t("app.askStrikeUp.context.widgets")} />
      </Flex>

      {/* Widget selector */}
      <Box ref={selectorRef}>
        <WidgetSelector
          selectedWidgetId={selectedWidgetId}
          onSelect={handleSelect}
        />
      </Box>

      {/* Sticky banner */}
      {selectedWidgetId && !loading && widget && !selectorInView && (
        <HStack
          position="fixed"
          top={0}
          left={`${bannerLeft}px`}
          right={0}
          zIndex={49}
          px={4}
          py={2}
          bg={{ base: "blue.50", _dark: "blue.950" }}
          borderBottomWidth="1px"
          borderColor={{ base: "blue.200", _dark: "blue.700" }}
          gap={2}
          boxShadow="sm"
        >
          <Text fontSize="xs" color={{ base: "blue.600", _dark: "blue.300" }}>
            Embed code for
          </Text>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={{ base: "blue.700", _dark: "blue.200" }}
          >
            {widget.name}
          </Text>
        </HStack>
      )}

      {/* Content */}
      {!selectedWidgetId ? (
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color="var(--text-secondary-color)"
              py={6}
              m={0}
            >
              Select a widget above to get its embed code.
            </Text>
          </Card.Body>
        </Card.Root>
      ) : loading ? (
        <Card.Root>
          <Card.Body>
            <Text textAlign="center" color="var(--text-primary)" m={0}>
              Loading…
            </Text>
          </Card.Body>
        </Card.Root>
      ) : widget ? (
        <>
          {/* Widget details / edit card */}
          <Card.Root>
            <Card.Body>
              <HStack justify="space-between" align="center" mb={4}>
                <VStack align="start" gap={0.5}>
                  <Text
                    fontFamily="var(--font-mono)"
                    fontSize="10px"
                    letterSpacing="0.10em"
                    textTransform="uppercase"
                    color="var(--text-secondary-color)"
                    m={0}
                  >
                    Widget
                  </Text>
                  <Heading size="md" color="var(--text-primary)" m={0}>
                    {isEditing ? "Edit Widget" : "Widget Details"}
                  </Heading>
                </VStack>
                <HStack gap={2}>
                  {!isEditing && CHAT_WIDGET_URL && widget.applicationToken && (
                    <Button
                      intent="secondary"
                      size="sm"
                      onClick={() =>
                        window.open(
                          `${CHAT_WIDGET_URL}/?applicationToken=${
                            widget.applicationToken
                          }&userId=preview_user_${Date.now()}`,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      <MdOpenInNew />
                      Preview widget
                    </Button>
                  )}
                  {!isEditing ? (
                    <Button
                      intent="secondary"
                      size="sm"
                      onClick={enterEditMode}
                    >
                      <MdEdit />
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button
                        intent="secondary"
                        size="sm"
                        onClick={cancelEdit}
                        disabled={editSaving}
                      >
                        <MdClose />
                        Cancel
                      </Button>
                      <Button
                        intent="primary"
                        size="sm"
                        onClick={handleSave}
                        loading={editSaving}
                      >
                        <MdSave />
                        Save
                      </Button>
                    </>
                  )}
                </HStack>
              </HStack>

              {/* View mode */}
              {!isEditing && (
                <Stack gap={3}>
                  <Box>
                    <Text
                      fontFamily="var(--font-mono)"
                      fontSize="10px"
                      letterSpacing="0.10em"
                      textTransform="uppercase"
                      color="var(--text-secondary-color)"
                      mb={1}
                    >
                      Name
                    </Text>
                    <Text fontSize="sm" color="var(--text-primary)" m={0}>
                      {widget.name}
                    </Text>
                  </Box>
                  {widget.description && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color={{ base: "gray.500", _dark: "gray.400" }}
                        mb={1}
                      >
                        Description
                      </Text>
                      <Text
                        fontSize="sm"
                        color={{ base: "gray.700", _dark: "gray.300" }}
                      >
                        {widget.description}
                      </Text>
                    </Box>
                  )}
                  <Box>
                    <Text
                      fontFamily="var(--font-mono)"
                      fontSize="10px"
                      letterSpacing="0.10em"
                      textTransform="uppercase"
                      color="var(--text-secondary-color)"
                      mb={1}
                    >
                      Display type
                    </Text>
                    <Badge colorPalette="blue" size="sm">
                      {widget.displayType ?? "popup"}
                    </Badge>
                  </Box>
                  {widget.serverMappings &&
                    widget.serverMappings.length > 0 && (
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={{ base: "gray.500", _dark: "gray.400" }}
                          mb={1}
                        >
                          Toolkits
                        </Text>
                        <HStack gap={1} flexWrap="wrap">
                          {widget.serverMappings.map((m) => (
                            <Badge
                              key={m.serverId}
                              colorPalette="purple"
                              size="sm"
                              variant="subtle"
                            >
                              {m.server?.name ?? "Server"}
                            </Badge>
                          ))}
                        </HStack>
                      </Box>
                    )}
                  {/* Knowledge Base */}
                  <Box>
                    <Text
                      fontFamily="var(--font-mono)"
                      fontSize="10px"
                      letterSpacing="0.10em"
                      textTransform="uppercase"
                      color="var(--text-secondary-color)"
                      mb={1}
                    >
                      Knowledge Base
                    </Text>
                    <HStack gap={1} flexWrap="wrap">
                      {(() => {
                        const allFolders = folderTree.flatMap((f) => [
                          f,
                          ...f.children,
                        ]);
                        const assignedDocs = allDocuments.filter(
                          (d) =>
                            d.isGlobal ||
                            (widget.kbDocumentIds ?? []).includes(d.id),
                        );
                        const assignedSources = allWebSources.filter(
                          (s) =>
                            s.isGlobal ||
                            (widget.kbWebSourceIds ?? []).includes(s.id),
                        );
                        const isEmpty =
                          (widget.kbFolderIds ?? []).length === 0 &&
                          assignedDocs.length === 0 &&
                          assignedSources.length === 0;
                        return (
                          <>
                            {/* Global folder badges */}
                            {allFolders
                              .filter((f) => f.isGlobal)
                              .map((f) => (
                                <Badge
                                  key={f.id}
                                  colorPalette="purple"
                                  size="sm"
                                  variant="subtle"
                                >
                                  {f.name} (Global,{" "}
                                  {f.documentCount + f.webSourceCount})
                                </Badge>
                              ))}
                            {/* Widget-assigned folder badges */}
                            {(widget.kbFolderIds ?? []).map((fid) => {
                              const folder = allFolders.find(
                                (f) => f.id === fid,
                              );
                              return folder && !folder.isGlobal ? (
                                <Badge
                                  key={fid}
                                  colorPalette="teal"
                                  size="sm"
                                  variant="subtle"
                                >
                                  {folder.name} (
                                  {folder.documentCount + folder.webSourceCount}
                                  )
                                </Badge>
                              ) : null;
                            })}
                            {/* Legacy individual doc badges */}
                            {assignedDocs.map((d) => (
                              <Badge
                                key={d.id}
                                colorPalette="teal"
                                size="sm"
                                variant="subtle"
                              >
                                {d.fileName}
                                {d.isGlobal && " (Global)"}
                              </Badge>
                            ))}
                            {/* Legacy individual web source badges */}
                            {assignedSources.map((s) => (
                              <Badge
                                key={s.id}
                                colorPalette="teal"
                                size="sm"
                                variant="subtle"
                              >
                                {s.name}
                                {s.isGlobal && " (Global)"}
                              </Badge>
                            ))}
                            {isEmpty && (
                              <Text
                                fontSize="sm"
                                color="var(--text-secondary-color)"
                                m={0}
                              >
                                None
                              </Text>
                            )}
                          </>
                        );
                      })()}
                    </HStack>
                  </Box>
                </Stack>
              )}

              {/* Edit mode */}
              {isEditing && (
                <Stack gap={4}>
                  {editError && (
                    <Box
                      p={3}
                      borderRadius="10px"
                      bg="rgba(239, 68, 68, 0.08)"
                      borderWidth="1px"
                      borderColor="var(--accent-danger, #EF4444)"
                    >
                      <Text fontSize="sm" color="var(--text-primary)" m={0}>
                        {editError}
                      </Text>
                    </Box>
                  )}

                  <Input
                    label="Name *"
                    value={editName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEditName(e.target.value)
                    }
                    placeholder="Widget name"
                  />

                  <Textarea
                    label="Description"
                    value={editDescription}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setEditDescription(e.target.value)
                    }
                    placeholder="Optional description"
                    rows={4}
                  />

                  {/* Toolkits */}
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight={500}
                      color="var(--text-primary)"
                      mb={1}
                    >
                      Toolkits *
                    </Text>
                    <Text
                      fontSize="xs"
                      color="var(--text-secondary-color)"
                      mb={2}
                    >
                      Select up to 5 toolkits.
                    </Text>
                    {allServers.filter((s) => s.isActive).length === 0 ? (
                      <Text
                        fontSize="sm"
                        color="var(--accent-danger, #EF4444)"
                        m={0}
                      >
                        No active toolkits available.
                      </Text>
                    ) : (
                      <Flex
                        direction="column"
                        gap={2}
                        p={3}
                        borderRadius="10px"
                        borderWidth="1px"
                        borderColor="var(--border-default)"
                      >
                        {allServers
                          .filter((s) => s.isActive)
                          .map((server) => (
                            <HStack key={server.id} align="center" gap={2}>
                              <Switch
                                checked={editServerIds.includes(server.id!)}
                                disabled={
                                  !editServerIds.includes(server.id!) &&
                                  editServerIds.length >= 5
                                }
                                onCheckedChange={(e: any) => {
                                  if (e.checked) {
                                    if (editServerIds.length >= 5) {
                                      setEditError(
                                        "Maximum 5 toolkits can be selected.",
                                      );
                                      return;
                                    }
                                    setEditServerIds([
                                      ...editServerIds,
                                      server.id!,
                                    ]);
                                    setEditError("");
                                  } else {
                                    setEditServerIds(
                                      editServerIds.filter(
                                        (id) => id !== server.id!,
                                      ),
                                    );
                                  }
                                }}
                                colorPalette="blue"
                              />
                              <Flex direction="column" flex={1}>
                                <Text
                                  fontSize="sm"
                                  fontWeight={500}
                                  color="var(--text-primary)"
                                  m={0}
                                >
                                  {server.name}
                                </Text>
                                {server.description && (
                                  <Text
                                    fontSize="xs"
                                    color="var(--text-secondary-color)"
                                    m={0}
                                  >
                                    {server.description}
                                  </Text>
                                )}
                              </Flex>
                            </HStack>
                          ))}
                      </Flex>
                    )}
                    <Text
                      fontSize="xs"
                      mt={1}
                      color={
                        editServerIds.length > 5
                          ? "var(--accent-danger, #EF4444)"
                          : "var(--text-secondary-color)"
                      }
                      m={0}
                    >
                      {editServerIds.length} of 5 toolkits selected
                    </Text>
                  </Box>

                  {/* Knowledge Base */}
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight={500}
                      color="var(--text-primary)"
                      mb={1}
                    >
                      Knowledge Base
                    </Text>
                    <Text
                      fontSize="xs"
                      color="var(--text-secondary-color)"
                      mb={2}
                    >
                      Select folders or individual items to include in this
                      widget's knowledge base.
                    </Text>
                    <KbTreePicker
                      folderTree={folderTree}
                      selectedFolderIds={editKbFolderIds}
                      onToggleFolder={(folderId) => {
                        // Collect folderId + all descendant IDs (any depth)
                        const collectIds = (
                          nodes: typeof folderTree,
                          id: string,
                        ): string[] => {
                          for (const node of nodes) {
                            if (node.id === id)
                              return [
                                id,
                                ...node.children.flatMap((c) =>
                                  collectIds(node.children, c.id),
                                ),
                              ];
                            const found = collectIds(node.children, id);
                            if (found.length) return found;
                          }
                          return [];
                        };
                        const ids = collectIds(folderTree, folderId);
                        setEditKbFolderIds((prev) =>
                          prev.includes(folderId)
                            ? prev.filter((id) => !ids.includes(id))
                            : [...prev, ...ids],
                        );
                      }}
                      rootDocuments={allDocuments.filter((d) => !d.folderId)}
                      rootWebSources={allWebSources.filter((s) => !s.folderId)}
                      selectedDocumentIds={editKbDocumentIds}
                      selectedWebSourceIds={editKbWebSourceIds}
                      onToggleDocument={(docId) => {
                        setEditKbDocumentIds((prev) =>
                          prev.includes(docId)
                            ? prev.filter((id) => id !== docId)
                            : [...prev, docId],
                        );
                      }}
                      onToggleWebSource={(wsId) => {
                        setEditKbWebSourceIds((prev) =>
                          prev.includes(wsId)
                            ? prev.filter((id) => id !== wsId)
                            : [...prev, wsId],
                        );
                      }}
                    />
                  </Box>
                </Stack>
              )}
            </Card.Body>
          </Card.Root>

          {/* Embed code */}
          <Card.Root>
            <Card.Body>
              <Box mb={1}>
                <PreHeader size="xs">Snippet</PreHeader>
              </Box>
              <Heading size="md" mb={2} color="var(--text-primary)" m={0}>
                Embed Code
              </Heading>
              <Text
                fontSize="sm"
                mb={4}
                color="var(--text-secondary-color)"
                m={0}
              >
                Paste this snippet into your website's HTML, just before the
                closing{" "}
                <Text
                  as="span"
                  fontFamily="var(--font-mono)"
                  fontSize="xs"
                  color="var(--text-primary)"
                >
                  &lt;/body&gt;
                </Text>{" "}
                tag.
              </Text>

              {/* Inline embed block */}
              <Box
                mb={4}
                p={3.5}
                borderWidth="1px"
                borderRadius="10px"
                borderColor="var(--border-default)"
                bg="var(--bg-secondary)"
              >
                <Box mb={1}>
                  <PreHeader size="xs">
                    {t("app.widgets.embedInline.preHeader")}
                  </PreHeader>
                </Box>
                <Flex align="center" justify="space-between" gap={3}>
                  <Box flex="1">
                    <Heading size="sm" mb={1} color="var(--text-primary)" m={0}>
                      {t("app.widgets.embedInline.title")}
                    </Heading>
                    <Text
                      fontSize="xs"
                      color="var(--text-secondary-color)"
                      m={0}
                    >
                      {t("app.widgets.embedInline.description")}
                    </Text>
                  </Box>
                  <Switch
                    checked={inlineEmbed}
                    onCheckedChange={(e: any) => setInlineEmbed(e.checked)}
                    colorPalette="blue"
                  />
                </Flex>

                {inlineEmbed && (
                  <Stack gap={3} mt={3}>
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight={500}
                        color="var(--text-primary)"
                        mb={1}
                      >
                        {t("app.widgets.embedInline.containerLabel")}
                      </Text>
                      <Input
                        value={inlineContainer}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInlineContainer(e.target.value)
                        }
                        placeholder="#strikeup-widget"
                        size="sm"
                      />
                    </Box>
                    <Box
                      p={2.5}
                      borderRadius="8px"
                      bg="rgba(59, 130, 246, 0.06)"
                      borderWidth="1px"
                      borderColor="var(--border-default)"
                    >
                      <Text
                        fontSize="xs"
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        {t("app.widgets.embedInline.tip")}
                      </Text>
                    </Box>
                    <Flex align="center" justify="space-between" gap={3}>
                      <Box flex="1">
                        <Text
                          fontSize="sm"
                          fontWeight={500}
                          color="var(--text-primary)"
                          m={0}
                        >
                          {t("app.widgets.embedInline.portalLabel")}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="var(--text-secondary-color)"
                          m={0}
                        >
                          {t("app.widgets.embedInline.portalDescription")}
                        </Text>
                      </Box>
                      <Switch
                        checked={inlinePortal}
                        onCheckedChange={(e: any) => setInlinePortal(e.checked)}
                        colorPalette="blue"
                      />
                    </Flex>
                  </Stack>
                )}
              </Box>

              {/* Auth injection toggles */}
              {widgetServers.length > 0 && (
                <Box
                  mb={4}
                  p={3.5}
                  borderWidth="1px"
                  borderRadius="10px"
                  borderColor="var(--border-default)"
                  bg="var(--bg-secondary)"
                >
                  <Box mb={1}>
                    <PreHeader size="xs">Inject auth</PreHeader>
                  </Box>
                  <Heading size="sm" mb={1} color="var(--text-primary)" m={0}>
                    Inject Authorization
                  </Heading>
                  <Text
                    fontSize="xs"
                    mb={3}
                    color="var(--text-secondary-color)"
                    m={0}
                  >
                    Include client-side authorization tokens for toolkits that
                    require them.
                  </Text>
                  <Stack gap={2}>
                    {widgetServers.map((server) => {
                      const active = injectedServerIds.includes(server.id!);
                      return (
                        <Box
                          key={server.id}
                          p={3}
                          borderRadius="10px"
                          borderWidth="1px"
                          borderColor={
                            active
                              ? "var(--brand-primary, #3B82F6)"
                              : "var(--border-default)"
                          }
                          bg={
                            active
                              ? "rgba(59, 130, 246, 0.06)"
                              : "var(--bg-primary)"
                          }
                          transition="background-color 0.15s, border-color 0.15s"
                        >
                          <Flex align="center" justify="space-between">
                            <Box flex="1">
                              <Text
                                fontSize="sm"
                                fontWeight={500}
                                color="var(--text-primary)"
                                m={0}
                              >
                                {server.name}
                              </Text>
                              <Text
                                fontSize="xs"
                                color="var(--text-secondary-color)"
                                m={0}
                              >
                                {server.authorizationType}
                              </Text>
                            </Box>
                            <Switch
                              checked={active}
                              onCheckedChange={(e: any) =>
                                toggleInjected(server.id!, e.checked)
                              }
                              colorPalette="blue"
                            />
                          </Flex>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Context injection — feed the AI hidden page / open-time context */}
              <ContextInjectionPanel />

              <Box
                p={3}
                borderRadius="10px"
                borderWidth="1px"
                borderColor="var(--border-default)"
                bg="var(--bg-secondary)"
                mb={3}
                overflowX="auto"
              >
                <Text
                  as="pre"
                  fontSize="xs"
                  fontFamily="var(--font-mono)"
                  whiteSpace="pre"
                  color="var(--text-primary)"
                  m={0}
                >
                  {buildEmbedScript(widget, injectedServers, {
                    enabled: inlineEmbed,
                    container: inlineContainer,
                    portal: inlinePortal,
                  })}
                </Text>
              </Box>
              <Button onClick={handleCopy} intent="secondary" size="sm">
                {copied ? <MdCheck /> : <MdContentCopy />}
                {copied ? "Copied!" : "Copy embed code"}
              </Button>

              {/* Custom trigger snippets — shown for both custom-trigger display types */}
              {isCustomTriggerDisplay(widget.displayType) && (
                <Box
                  mt={6}
                  pt={5}
                  borderTopWidth="1px"
                  borderColor="var(--border-default)"
                >
                  <Box mb={1}>
                    <PreHeader size="xs">Triggers</PreHeader>
                  </Box>
                  <Heading size="sm" mb={1} color="var(--text-primary)" m={0}>
                    Custom Trigger Button Snippets
                  </Heading>
                  <Text
                    fontSize="sm"
                    mb={5}
                    color="var(--text-secondary-color)"
                    m={0}
                  >
                    This widget has no floating button. Add these snippets to
                    any element on your page to open and close the AI pane.
                  </Text>

                  <Stack gap={6}>
                    {/* 1 — Plain open */}
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight={500}
                        mb={1}
                        color="var(--text-primary)"
                        m={0}
                      >
                        Open widget
                      </Text>
                      <Text
                        fontSize="xs"
                        mb={2}
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        Opens the AI pane with no message.
                      </Text>
                      <Box
                        p={3}
                        borderRadius="10px"
                        borderWidth="1px"
                        borderColor="var(--border-default)"
                        bg="var(--bg-secondary)"
                        mb={2}
                      >
                        <Text
                          as="pre"
                          fontSize="xs"
                          fontFamily="var(--font-mono)"
                          whiteSpace="pre-wrap"
                          color="var(--text-primary)"
                          m={0}
                        >
                          {triggerOpenBasic}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        intent="secondary"
                        onClick={() =>
                          copySnippet(triggerOpenBasic, setCopiedTriggerBasic)
                        }
                      >
                        {copiedTriggerBasic ? <MdCheck /> : <MdContentCopy />}
                        {copiedTriggerBasic ? "Copied!" : "Copy"}
                      </Button>
                    </Box>

                    {/* 2 — Open with placeholder message */}
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight={500}
                        mb={1}
                        color="var(--text-primary)"
                        m={0}
                      >
                        Open with a placeholder AI message
                      </Text>
                      <Text
                        fontSize="xs"
                        mb={3}
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        The pane opens and shows a fake AI message bubble. The
                        user reads it and types their reply — no AI call is made
                        until they send.
                      </Text>
                      <Box mb={3}>
                        <Input
                          value={triggerMessage}
                          onChange={(e) => setTriggerMessage(e.target.value)}
                          placeholder="What would you like to know about this page?"
                          size="sm"
                        />
                      </Box>
                      <Flex
                        align="center"
                        justify="space-between"
                        gap={3}
                        mb={3}
                      >
                        <Box flex="1">
                          <Text
                            fontSize="sm"
                            fontWeight={500}
                            color="var(--text-primary)"
                            m={0}
                          >
                            {t("app.widgets.embedTriggerAutoSend.title")}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="var(--text-secondary-color)"
                            m={0}
                          >
                            {t("app.widgets.embedTriggerAutoSend.description")}
                          </Text>
                        </Box>
                        <Switch
                          checked={triggerAutoSend}
                          onCheckedChange={(e: any) =>
                            setTriggerAutoSend(e.checked)
                          }
                          colorPalette="blue"
                        />
                      </Flex>
                      <Box
                        p={3}
                        borderRadius="10px"
                        borderWidth="1px"
                        borderColor="var(--border-default)"
                        bg="var(--bg-secondary)"
                        mb={2}
                      >
                        <Text
                          as="pre"
                          fontSize="xs"
                          fontFamily="var(--font-mono)"
                          whiteSpace="pre-wrap"
                          color="var(--text-primary)"
                          m={0}
                        >
                          {triggerOpenMessage}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        intent="secondary"
                        onClick={() =>
                          copySnippet(
                            triggerOpenMessage,
                            setCopiedTriggerMessage,
                          )
                        }
                      >
                        {copiedTriggerMessage ? <MdCheck /> : <MdContentCopy />}
                        {copiedTriggerMessage ? "Copied!" : "Copy"}
                      </Button>
                    </Box>

                    {/* 3 — Open with developer context (hidden from user, fed to AI) */}
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight={500}
                        mb={1}
                        color="var(--text-primary)"
                        m={0}
                      >
                        Open with a developer message (hidden context for the
                        AI)
                      </Text>
                      <Text
                        fontSize="xs"
                        mb={3}
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        Pass{" "}
                        <Text as="code" fontFamily="mono">
                          openContext
                        </Text>{" "}
                        alongside the user-facing message. It is invisible to
                        the user and injected as system context for the AI on
                        the opening turn only — one sentence about why this
                        button was pressed. Describe the page itself with{" "}
                        <Text as="code" fontFamily="mono">
                          context.set()
                        </Text>{" "}
                        (above) so it outlives the first reply.
                      </Text>
                      <Box mb={3}>
                        <Input
                          value={devContextExample}
                          onChange={(e) => setDevContextExample(e.target.value)}
                          placeholder="Visitor clicked 'Explain this chart' on the Revenue graph…"
                          size="sm"
                        />
                      </Box>
                      <Box
                        p={3}
                        borderRadius="10px"
                        borderWidth="1px"
                        borderColor="var(--border-default)"
                        bg="var(--bg-secondary)"
                        mb={2}
                      >
                        <Text
                          as="pre"
                          fontSize="xs"
                          fontFamily="var(--font-mono)"
                          whiteSpace="pre-wrap"
                          color="var(--text-primary)"
                          m={0}
                        >
                          {triggerOpenDevContext}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        intent="secondary"
                        onClick={() =>
                          copySnippet(
                            triggerOpenDevContext,
                            setCopiedTriggerDevCtx,
                          )
                        }
                      >
                        {copiedTriggerDevCtx ? <MdCheck /> : <MdContentCopy />}
                        {copiedTriggerDevCtx ? "Copied!" : "Copy"}
                      </Button>
                    </Box>

                    {/* 4 — Read open/closed state */}
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight={500}
                        mb={1}
                        color="var(--text-primary)"
                        m={0}
                      >
                        {t("app.widgets.embedTriggerState.title")}
                      </Text>
                      <Text
                        fontSize="xs"
                        mb={2}
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        {t("app.widgets.embedTriggerState.description")}
                      </Text>
                      <Box
                        p={3}
                        borderRadius="10px"
                        borderWidth="1px"
                        borderColor="var(--border-default)"
                        bg="var(--bg-secondary)"
                        mb={2}
                      >
                        <Text
                          as="pre"
                          fontSize="xs"
                          fontFamily="var(--font-mono)"
                          whiteSpace="pre-wrap"
                          color="var(--text-primary)"
                          m={0}
                        >
                          {triggerIsOpen}
                        </Text>
                      </Box>
                      <Text
                        fontSize="xs"
                        mb={2}
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        {t("app.widgets.embedTriggerState.pollingNote")}
                      </Text>
                      <Button
                        size="sm"
                        intent="secondary"
                        onClick={() =>
                          copySnippet(triggerIsOpen, setCopiedTriggerIsOpen)
                        }
                      >
                        {copiedTriggerIsOpen ? <MdCheck /> : <MdContentCopy />}
                        {copiedTriggerIsOpen ? "Copied!" : "Copy"}
                      </Button>
                    </Box>

                    {/* 5 — Close */}
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight={500}
                        mb={1}
                        color="var(--text-primary)"
                        m={0}
                      >
                        Close widget
                      </Text>
                      <Text
                        fontSize="xs"
                        mb={2}
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        Closes the pane programmatically — useful for a
                        dedicated close button elsewhere on the page.
                      </Text>
                      <Box
                        p={3}
                        borderRadius="10px"
                        borderWidth="1px"
                        borderColor="var(--border-default)"
                        bg="var(--bg-secondary)"
                        mb={2}
                      >
                        <Text
                          as="pre"
                          fontSize="xs"
                          fontFamily="var(--font-mono)"
                          whiteSpace="pre-wrap"
                          color="var(--text-primary)"
                          m={0}
                        >
                          {triggerClose}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        intent="secondary"
                        onClick={() =>
                          copySnippet(triggerClose, setCopiedTriggerClose)
                        }
                      >
                        {copiedTriggerClose ? <MdCheck /> : <MdContentCopy />}
                        {copiedTriggerClose ? "Copied!" : "Copy"}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Card.Body>
          </Card.Root>
        </>
      ) : null}
    </Stack>
  );
};

export default EmbedPage;
